import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import CreateActionButton from '@/components/patterns/create-action-button'
import { CustomerTypeBadge } from '@/components/customers/CustomerTypeBadge'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import {
  IndexDataTable,
  getIndexPaginationMeta,
  IndexTableClearFiltersButton,
  IndexTableExportButton,
  IndexTablePagination,
  IndexTableSearchBox,
  IndexTableToolbar,
} from '@/components/tables/IndexTable'
import {
  createAddressExportColumns,
  createIndexTableExportColumn,
  fetchAllIndexTablePages,
  fetchIndexTableQueryPage,
  useIndexTableSpreadsheetExport,
} from '@/components/tables/indexTableExport'
import { useIndexTableSearchParams } from '@/components/tables/useIndexTableSearchParams'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { usePhoneFormatter } from '@/hooks/use-phone-formatter'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can, useAbility } from '@/lib/casl'
import { canViewCustomerReceivables } from '@/lib/customer-receivables-access'
import dayjs from '@/lib/dayjs'
import { CustomerPaymentTerms } from '@/lib/enums/CustomerPaymentTerms'
import queryClient from '@/lib/queryClient'
import { prefetchIndexTableRouteQuery } from '@/lib/services/index-table-query-options'
import { customerQueries, useCustomers } from '@/lib/services/customers/customers.query'
import { cn, formatCurrency, getEnumDisplayName, parseCurrency } from '@/lib/utils'
import {
  createFileRoute,
  Link,
  notFound,
  stripSearchParams,
  useNavigate,
} from '@tanstack/react-router'
import {
  ArrowDownAZ,
  Building2,
  CalendarClock,
  DollarSign,
  Mail,
  Phone,
  ReceiptText,
  ShoppingCart,
  SlidersHorizontal,
  User,
  Users,
} from 'lucide-react'
import { z } from 'zod/v4'
import { useMemo } from 'react'

const DEFAULT_PAGE_SIZE = 20
const defaultCustomerListSearchParams = {
  filter: 'all',
  sort: 'first_name',
  page: 1,
  page_size: DEFAULT_PAGE_SIZE,
}
const strippedCustomerListSearchParams = {
  filter: defaultCustomerListSearchParams.filter,
  page: defaultCustomerListSearchParams.page,
  page_size: defaultCustomerListSearchParams.page_size,
}
const customerFilterValues = [
  'all',
  'open_balance',
  'business',
  'individual',
  'with_orders',
  'without_orders',
]
const customerSortValues = [
  'first_name',
  'first_name_asc',
  'first_name_desc',
  'recent_orders',
  'recent_orders_desc',
  'recent_orders_asc',
  'open_balance_desc',
  'open_balance_asc',
  'total_orders_desc',
  'total_orders_asc',
  'newest_customers',
  'oldest_customers',
]
const customerListUrlSearchSchema = z.object({
  search: z.string().optional().catch(undefined),
  filter: z.enum(customerFilterValues).catch(defaultCustomerListSearchParams.filter),
  sort: z.enum(customerSortValues).catch(defaultCustomerListSearchParams.sort),
  page: z.coerce.number().int().positive().catch(defaultCustomerListSearchParams.page),
  page_size: z.coerce.number().int().positive().catch(defaultCustomerListSearchParams.page_size),
})

const filterPresets = [
  { value: 'all', label: 'All', icon: SlidersHorizontal },
  { value: 'open_balance', label: 'Open Balance', icon: DollarSign },
  { value: 'business', label: 'Businesses', icon: Building2 },
  { value: 'individual', label: 'Individuals', icon: User },
  { value: 'with_orders', label: 'Past Orders', icon: ShoppingCart },
  { value: 'without_orders', label: 'No Orders', icon: ReceiptText },
]

const sortOptions = [
  { value: 'first_name', label: 'Name A-Z', icon: ArrowDownAZ },
  { value: 'first_name_desc', label: 'Name Z-A', icon: ArrowDownAZ },
  { value: 'recent_orders', label: 'Last Order Newest', icon: CalendarClock },
  { value: 'recent_orders_asc', label: 'Last Order Oldest', icon: CalendarClock },
  { value: 'open_balance_desc', label: 'Open Balance High-Low', icon: DollarSign },
  { value: 'open_balance_asc', label: 'Open Balance Low-High', icon: DollarSign },
  { value: 'total_orders_desc', label: 'Past Orders High-Low', icon: ShoppingCart },
  { value: 'total_orders_asc', label: 'Past Orders Low-High', icon: ShoppingCart },
  { value: 'newest_customers', label: 'Newest Customers', icon: Users },
  { value: 'oldest_customers', label: 'Oldest Customers', icon: Users },
]

const customerDirectorySortColumns = {
  customer: {
    asc: 'first_name',
    ascAliases: ['first_name_asc'],
    desc: 'first_name_desc',
    defaultSort: 'first_name',
  },
  lastOrder: {
    asc: 'recent_orders_asc',
    desc: 'recent_orders',
    descAliases: ['recent_orders_desc'],
    defaultSort: 'recent_orders',
  },
  orders: {
    asc: 'total_orders_asc',
    desc: 'total_orders_desc',
    defaultSort: 'total_orders_desc',
  },
  balance: {
    asc: 'open_balance_asc',
    desc: 'open_balance_desc',
    defaultSort: 'open_balance_desc',
  },
}

export const Route = createFileRoute('/_authenticated/$orgId/customers/')({
  component: CustomersIndexPage,
  validateSearch: (search) => customerListUrlSearchSchema.parse(search),
  search: {
    middlewares: [stripSearchParams(strippedCustomerListSearchParams)],
  },
  loader: async ({ context, params }) => {
    if (!context.ability.can('list', 'customers')) throw notFound()
    void prefetchIndexTableRouteQuery(
      context.queryClient,
      customerQueries.index({
        organizationId: params.orgId,
        filters: getCustomerListFilters({}),
      })
    )

    return { isReadOnly: false }
  },
})

function getOpenBalance(customer) {
  return parseCurrency(customer?.metrics?.open_balance ?? 0)
}

function getCustomerCreditLimit(customer) {
  if (
    customer?.pay_later_allowed === false ||
    customer?.credit_limit === null ||
    customer?.credit_limit === undefined ||
    customer?.credit_limit === ''
  ) {
    return null
  }

  return parseCurrency(customer.credit_limit)
}

function getCustomerBalanceTone({ openBalance, pastDueBalance, creditLimit }) {
  if (openBalance <= 0) {
    return 'neutral'
  }

  if (creditLimit !== null && openBalance > creditLimit) {
    return 'danger'
  }

  if (pastDueBalance > 0) {
    return 'warning'
  }

  return 'neutral'
}

function getCustomerBalanceTextClass(tone) {
  return {
    danger: 'text-status-destructive-foreground dark:text-status-destructive-foreground',
    warning: 'text-status-warning-foreground dark:text-status-warning-foreground',
    neutral: '',
  }[tone]
}

function getCustomersFromResponse(response) {
  return Array.isArray(response) ? response : (response?.data ?? [])
}

function getCustomerIndexSummary(response) {
  return Array.isArray(response)
    ? {
        open_balance_total: '0.00',
        past_due_balance_total: '0.00',
      }
    : (response?.summary ?? {
        open_balance_total: '0.00',
        past_due_balance_total: '0.00',
      })
}

function getCustomerListSearchParams(search) {
  return customerListUrlSearchSchema.parse(search ?? {})
}

function getCustomerListFilters(search) {
  const parsedSearch = getCustomerListSearchParams(search)
  const trimmedSearch = parsedSearch.search?.trim()

  return {
    ...(trimmedSearch ? { search: trimmedSearch } : {}),
    filter: parsedSearch.filter,
    sort: parsedSearch.sort,
    page: parsedSearch.page,
    page_size: parsedSearch.page_size,
  }
}

function customerCountText(total) {
  return `${total} customer${total === 1 ? '' : 's'}`
}

function customerCanUseFinancialSearch(value) {
  return value === 'open_balance' || value === 'open_balance_desc' || value === 'open_balance_asc'
}

function normalizeCustomerReceivablesSearch(search, canViewReceivables) {
  if (canViewReceivables) {
    return search
  }

  return {
    ...search,
    filter: customerCanUseFinancialSearch(search.filter) ? 'all' : search.filter,
    sort: customerCanUseFinancialSearch(search.sort)
      ? defaultCustomerListSearchParams.sort
      : search.sort,
  }
}

function CustomerFilterSelect({ options, value, onValueChange }) {
  const isFiltered = value !== 'all'

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger aria-label="Filter customers" selected={isFiltered} className="w-fit">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((preset) => {
          const Icon = preset.icon

          return (
            <SelectItem key={preset.value} value={preset.value}>
              <Icon className="size-4" />
              {preset.label}
            </SelectItem>
          )
        })}
      </SelectContent>
    </Select>
  )
}

function CustomerSortSelect({ options, value, onValueChange }) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger aria-label="Sort customers" className="w-fit">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => {
          const Icon = option.icon

          return (
            <SelectItem key={option.value} value={option.value}>
              <Icon className="size-4" />
              {option.label}
            </SelectItem>
          )
        })}
      </SelectContent>
    </Select>
  )
}

function CustomerIndexSummaryBadge({ icon: Icon, label, amount, tone = 'neutral', link }) {
  const toneClasses = {
    warning:
      'bg-status-warning-background text-status-warning-foreground dark:bg-status-warning-background dark:text-status-warning-foreground',
    destructive:
      'bg-status-destructive-background text-status-destructive-foreground dark:bg-status-destructive-background dark:text-status-destructive-foreground',
    neutral: 'bg-muted/45 text-foreground dark:bg-muted/25',
  }
  const iconClasses = {
    warning:
      'bg-status-warning-background text-status-warning-foreground dark:bg-status-warning-background dark:text-status-warning-foreground',
    destructive:
      'bg-status-destructive-background text-status-destructive-foreground dark:bg-status-destructive-background dark:text-status-destructive-foreground',
    neutral: 'bg-muted text-muted-foreground',
  }

  const className = cn(
    'inline-flex items-center gap-2 rounded-full px-2.5 py-1 transition-colors',
    link && 'hover:bg-muted/40',
    toneClasses[tone]
  )
  const content = (
    <>
      <span
        className={cn(
          'flex size-6 shrink-0 items-center justify-center rounded-full',
          iconClasses[tone]
        )}
      >
        <Icon className="size-3.5" />
      </span>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <span className="font-semibold tabular-nums">{formatCurrency(parseCurrency(amount))}</span>
    </>
  )

  if (link) {
    return (
      <Link {...link} className={className}>
        {content}
      </Link>
    )
  }

  return <div className={className}>{content}</div>
}

function CustomerIndexSummaryBadges({ summary, reportLink }) {
  const pastDueBalance = parseCurrency(summary.past_due_balance_total)

  return (
    <div className="flex flex-wrap items-center gap-2">
      <CustomerIndexSummaryBadge
        icon={DollarSign}
        label="Total open balance"
        amount={summary.open_balance_total}
        tone="warning"
        link={reportLink}
      />
      <CustomerIndexSummaryBadge
        icon={CalendarClock}
        label="Total past due"
        amount={summary.past_due_balance_total}
        tone={pastDueBalance > 0 ? 'destructive' : 'neutral'}
        link={reportLink}
      />
    </div>
  )
}

function getCustomerDirectoryColumns(formatPhone, canViewReceivables) {
  return [
    {
      id: 'customer',
      header: 'Customer',
      cell: ({ row }) => {
        const customer = row.original

        return (
          <>
            <Typography
              variant="body"
              weight="semibold"
              interaction="hoverUnderline"
              className="block truncate"
            >
              {customer.name || `Customer #${customer.id}`}
            </Typography>
            <Typography variant="caption" className="mt-1 block">
              {getCustomerSinceValue(customer)
                ? `Customer since ${dayjs(getCustomerSinceValue(customer)).format('MMM D, YYYY')}`
                : 'Customer'}
            </Typography>
          </>
        )
      },
      meta: {
        label: 'Customer',
        kind: 'primary',
        icon: ArrowDownAZ,
        sortable: customerDirectorySortColumns.customer,
        exportColumns: [
          {
            label: 'Customer',
            value: (customer) => customer.name || `Customer #${customer.id}`,
          },
          {
            label: 'Customer Since',
            value: getCustomerSinceValue,
            type: 'date',
          },
        ],
      },
    },
    {
      id: 'type',
      header: 'Type',
      cell: ({ row }) => <CustomerTypeBadge isBusiness={Boolean(row.original.is_business)} />,
      meta: {
        label: 'Type',
        kind: 'badge',
        icon: Building2,
        align: 'center',
        exportValue: (customer) => (customer.is_business ? 'Business' : 'Individual'),
      },
    },
    {
      id: 'contact',
      header: 'Contact',
      cell: ({ row }) => {
        const customer = row.original

        return (
          <div className="space-y-1">
            <div className="flex min-w-0 items-center gap-2">
              <Mail className="size-4 shrink-0 text-muted-foreground" />
              <span className="truncate">{customer.email || '(No email)'}</span>
            </div>
            <div className="flex min-w-0 items-center gap-2 text-muted-foreground">
              <Phone className="size-4 shrink-0 text-muted-foreground" />
              <span className="truncate">
                {customer.phone ? formatPhone(customer.phone) : '(No phone)'}
              </span>
            </div>
          </div>
        )
      },
      meta: {
        label: 'Contact',
        kind: 'text',
        icon: Mail,
        exportColumns: [
          {
            label: 'Email',
            value: (customer) => customer.email || '',
          },
          {
            label: 'Phone',
            value: (customer) => (customer.phone ? formatPhone(customer.phone) : ''),
          },
        ],
      },
    },
    {
      id: 'last_order',
      header: 'Last Order',
      cell: ({ row }) => {
        const customer = row.original
        const lastOrder = customer?.metrics?.last_activity_at
          ? dayjs(customer.metrics.last_activity_at).format('MMM D, YYYY')
          : 'No orders'

        return (
          <span className="whitespace-nowrap tabular-nums text-muted-foreground">{lastOrder}</span>
        )
      },
      meta: {
        label: 'Last Order',
        kind: 'date',
        icon: CalendarClock,
        sortable: customerDirectorySortColumns.lastOrder,
        align: 'center',
        contentAlign: 'start',
        contentClassName: 'lg:w-[12ch]',
        exportValue: (customer) => customer?.metrics?.last_activity_at ?? '',
        exportType: 'date',
      },
    },
    {
      id: 'orders',
      header: 'Orders',
      cell: ({ row }) => (
        <span className="font-semibold tabular-nums">
          {Number(row.original?.metrics?.total_orders ?? 0)}
        </span>
      ),
      meta: {
        label: 'Orders',
        kind: 'number',
        icon: ShoppingCart,
        sortable: customerDirectorySortColumns.orders,
        align: 'center',
        contentAlign: 'end',
        contentClassName: 'lg:w-[4ch]',
        exportValue: (customer) => Number(customer?.metrics?.total_orders ?? 0),
        exportType: 'integer',
      },
    },
    canViewReceivables
      ? {
          id: 'balance',
          header: 'Balance',
          cell: ({ row }) => {
            const customer = row.original
            const openBalance = getOpenBalance(customer)
            const pastDueBalance = parseCurrency(customer?.summary?.overdue_balance_total ?? 0)
            const creditLimit = getCustomerCreditLimit(customer)
            const hasOpenBalance = openBalance > 0
            const balanceTone = getCustomerBalanceTone({ openBalance, pastDueBalance, creditLimit })
            const balanceTextClass = getCustomerBalanceTextClass(balanceTone)

            return (
              <>
                <span className={cn('font-semibold tabular-nums', balanceTextClass)}>
                  {hasOpenBalance
                    ? `${formatCurrency(openBalance)} open`
                    : formatCurrency(openBalance)}
                </span>
                {pastDueBalance > 0 ? (
                  <Typography variant="label" className={cn('mt-1 block', balanceTextClass)}>
                    {formatCurrency(pastDueBalance)} past due
                  </Typography>
                ) : null}
              </>
            )
          },
          meta: {
            label: 'Balance',
            mobileLabel: 'Open Balance',
            kind: 'money',
            icon: DollarSign,
            sortable: customerDirectorySortColumns.balance,
            align: 'end',
            exportColumns: [
              {
                label: 'Open Balance',
                value: getOpenBalance,
                type: 'currency',
              },
              {
                label: 'Past Due Balance',
                value: (customer) => parseCurrency(customer?.summary?.overdue_balance_total ?? 0),
                type: 'currency',
              },
            ],
          },
        }
      : null,
  ].filter(Boolean)
}

function getCustomerSinceValue(customer) {
  return customer.customer_since_on || customer.created_at
}

function getCustomerPaymentTermsLabel(customer) {
  return customer?.payment_terms_code
    ? getEnumDisplayName(CustomerPaymentTerms, customer.payment_terms_code)
    : ''
}

function getCustomerExportOnlyColumns(formatPhone, canViewReceivables) {
  return [
    createIndexTableExportColumn({
      id: 'customer_id',
      label: 'Customer ID',
      type: 'integer',
      value: (customer) => customer.id,
    }),
    createIndexTableExportColumn({
      id: 'source_type',
      label: 'Source Type',
      value: (customer) => customer.source_type || '',
    }),
    createIndexTableExportColumn({
      id: 'billing_email',
      label: 'Billing Email',
      value: (customer) => customer.billing_email || '',
    }),
    createIndexTableExportColumn({
      id: 'pay_later_allowed',
      label: 'Pay Later Allowed',
      type: 'boolean',
      value: (customer) => customer.pay_later_allowed !== false,
    }),
    createIndexTableExportColumn({
      id: 'allow_fulfillment_before_payment',
      label: 'Allow Fulfillment Before Payment',
      type: 'boolean',
      value: (customer) => Boolean(customer.allow_fulfillment_before_payment),
    }),
    createIndexTableExportColumn({
      id: 'credit_limit',
      label: 'Credit Limit',
      type: 'currency',
      value: (customer) =>
        customer.credit_limit === null || customer.credit_limit === undefined
          ? null
          : parseCurrency(customer.credit_limit),
    }),
    createIndexTableExportColumn({
      id: 'payment_terms',
      label: 'Payment Terms',
      value: getCustomerPaymentTermsLabel,
    }),
    createIndexTableExportColumn({
      id: 'payment_terms_code',
      label: 'Payment Terms Code',
      value: (customer) => customer.payment_terms_code || '',
    }),
    createIndexTableExportColumn({
      id: 'payment_terms_days',
      label: 'Payment Terms Days',
      type: 'integer',
      value: (customer) => customer.payment_terms_days ?? null,
    }),
    createIndexTableExportColumn({
      id: 'tax_exempt',
      label: 'Tax Exempt',
      type: 'boolean',
      value: (customer) => Boolean(customer.tax_exempt),
    }),
    createIndexTableExportColumn({
      id: 'accepts_marketing',
      label: 'Accepts Marketing',
      type: 'boolean',
      value: (customer) => Boolean(customer.accepts_marketing),
    }),
    createIndexTableExportColumn({
      id: 'lifetime_customer_value',
      label: 'Lifetime Customer Value',
      type: 'currency',
      value: (customer) => parseCurrency(customer?.metrics?.lifetime_customer_value ?? 0),
    }),
    canViewReceivables
      ? createIndexTableExportColumn({
          id: 'receivables_open_balance',
          label: 'Receivables Open Balance',
          type: 'currency',
          value: (customer) => parseCurrency(customer?.summary?.open_balance_total ?? 0),
        })
      : null,
    canViewReceivables
      ? createIndexTableExportColumn({
          id: 'receivables_overdue_balance',
          label: 'Receivables Overdue Balance',
          type: 'currency',
          value: (customer) => parseCurrency(customer?.summary?.overdue_balance_total ?? 0),
        })
      : null,
    createIndexTableExportColumn({
      id: 'physical_address_is_billing',
      label: 'Physical Address Is Billing',
      type: 'boolean',
      value: (customer) => Boolean(customer.physical_address_is_billing),
    }),
    ...createAddressExportColumns({
      idPrefix: 'physical_address',
      labelPrefix: 'Physical Address',
      getAddress: (customer) => customer.physical_address,
    }),
    ...createAddressExportColumns({
      idPrefix: 'billing_address',
      labelPrefix: 'Billing Address',
      getAddress: (customer) => customer.billing_address,
    }),
    createIndexTableExportColumn({
      id: 'primary_contact_name',
      label: 'Primary Contact Name',
      value: (customer) => customer.primary_contact?.name || '',
    }),
    createIndexTableExportColumn({
      id: 'primary_contact_email',
      label: 'Primary Contact Email',
      value: (customer) => customer.primary_contact?.email || '',
    }),
    createIndexTableExportColumn({
      id: 'primary_contact_phone',
      label: 'Primary Contact Phone',
      value: (customer) =>
        customer.primary_contact?.phone ? formatPhone(customer.primary_contact.phone) : '',
    }),
  ].filter(Boolean)
}

export function CustomersIndexPage() {
  const ability = useAbility()
  const path = usePathHelper()
  const navigate = useNavigate()
  const { formatPhone } = usePhoneFormatter()
  const { orgId } = Route.useParams()
  const customerBalanceReportLink = path.customerBalances.index().using({ orgId }).link
  const canViewReceivables = Boolean(canViewCustomerReceivables(ability))
  const urlSearch = normalizeCustomerReceivablesSearch(
    getCustomerListSearchParams(Route.useSearch()),
    canViewReceivables
  )
  const urlSearchTerm = urlSearch.search ?? ''
  const { updatePage, updatePageSize, updateSearchTerm, updateSort, updateTableParams } =
    useIndexTableSearchParams({
      navigate,
      getSearchParams: getCustomerListSearchParams,
      searchTerm: urlSearchTerm,
    })

  const customerFilters = getCustomerListFilters(urlSearch)
  const hasActiveSearch = urlSearchTerm.trim().length > 0
  const {
    data: customersResponse,
    isFetching,
    isPending,
  } = useCustomers({
    filters: customerFilters,
  })
  const customers = getCustomersFromResponse(customersResponse)
  const customerSummary = getCustomerIndexSummary(customersResponse)
  const paginationMeta = getIndexPaginationMeta(
    customersResponse,
    urlSearch.page,
    urlSearch.page_size
  )
  const filterOptions = canViewReceivables
    ? filterPresets
    : filterPresets.filter((option) => !customerCanUseFinancialSearch(option.value))
  const sortOptionList = canViewReceivables
    ? sortOptions
    : sortOptions.filter((option) => !customerCanUseFinancialSearch(option.value))
  // TanStack renders cell functions as React components. Keep their identity stable
  // across preloads/refetches; remounting cells caused observed WebKit scroll jumps.
  const columns = useMemo(
    () => getCustomerDirectoryColumns(formatPhone, canViewReceivables),
    [formatPhone, canViewReceivables]
  )
  const exportColumns = [
    ...columns,
    ...getCustomerExportOnlyColumns(formatPhone, canViewReceivables),
  ]
  const { exportDisabled, exportPending, handleExport } = useIndexTableSpreadsheetExport({
    columns: exportColumns,
    rowCount: paginationMeta.total,
    filename: 'customers.xlsx',
    sheetName: 'Customers',
    loadRows: ({ signal } = {}) =>
      fetchAllIndexTablePages({
        signal,
        fetchPage: ({ page, pageSize, signal }) =>
          fetchIndexTableQueryPage(
            customerQueries.index({
              organizationId: orgId,
              filters: {
                ...customerFilters,
                page,
                page_size: pageSize,
              },
            }),
            { signal }
          ),
      }),
  })

  const handleFilterChange = (value) => updateTableParams({ filter: value })

  const clearFilters = () => updateTableParams({ search: undefined, filter: 'all' })

  const handlePageIntent = (page) => {
    void queryClient.prefetchQuery(
      customerQueries.index({
        organizationId: orgId,
        filters: getCustomerListFilters({ ...urlSearch, page }),
      })
    )
  }

  return (
    <div className="space-y-4">
      <AdminPageHeader
        icon={Users}
        title="Customers"
        metrics={
          canViewReceivables ? (
            <IfGlobalOrg show={false}>
              <CustomerIndexSummaryBadges
                summary={customerSummary}
                reportLink={customerBalanceReportLink}
              />
            </IfGlobalOrg>
          ) : null
        }
        actions={
          <IfGlobalOrg show={false}>
            <Can I="create" a="customers">
              <CreateActionButton asChild>
                <Link {...path.customers.create().link}>Create Customer</Link>
              </CreateActionButton>
            </Can>
          </IfGlobalOrg>
        }
      />
      <IfGlobalOrg
        show={false}
        fallback={
          <OrganizationContextNotice className="mb-4">
            Customers are organization-specific.
          </OrganizationContextNotice>
        }
      >
        <div className="space-y-4">
          <IndexDataTable
            data={customers}
            columns={columns}
            sort={urlSearch.sort}
            onSortChange={updateSort}
            rowLink={(customer) => path.customers.show(customer.id).link}
            getRowId={(customer) => String(customer.id)}
            isLoading={isPending}
            toolbar={
              <IndexTableToolbar
                primary={
                  <IndexTableSearchBox
                    value={urlSearchTerm}
                    onCommit={updateSearchTerm}
                    countText={customerCountText(paginationMeta.total)}
                    isFetching={isFetching}
                    placeholder="Search customers..."
                  />
                }
                secondary={
                  <>
                    {hasActiveSearch || urlSearch.filter !== 'all' ? (
                      <IndexTableClearFiltersButton onClick={clearFilters} />
                    ) : null}
                    <CustomerFilterSelect
                      options={filterOptions}
                      value={urlSearch.filter}
                      onValueChange={handleFilterChange}
                    />
                    <div className="lg:hidden">
                      <CustomerSortSelect
                        options={sortOptionList}
                        value={urlSearch.sort}
                        onValueChange={updateSort}
                      />
                    </div>
                  </>
                }
                actions={
                  <IndexTableExportButton
                    disabled={exportDisabled}
                    isPending={exportPending}
                    onClick={handleExport}
                  />
                }
              />
            }
            emptyState={<Typography variant="supporting">No customers found.</Typography>}
            tableClassName="text-sm"
          />

          <IndexTablePagination
            meta={paginationMeta}
            pageSize={urlSearch.page_size}
            onPageChange={updatePage}
            onPageIntent={handlePageIntent}
            onPageSizeChange={updatePageSize}
          />
        </div>
      </IfGlobalOrg>
    </div>
  )
}
